import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Award, GraduationCap, Sparkles, Star, CheckCircle2, ArrowRight, Quote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, CLINIC } from "@/lib/api";

const credentials = [
  { icon: GraduationCap, title: "BDS", subtitle: "Bachelor of Dental Surgery" },
  { icon: GraduationCap, title: "MIDA", subtitle: "Oro-Dental Surgeon" },
  { icon: Award, title: "Implantology", subtitle: "Certified Implantologist" },
  { icon: Sparkles, title: "Orthodontics", subtitle: "Certified Orthodontist" },
];

const expertise = [
  "Painless Root Canal Treatment (Single-Sitting RCT)",
  "Dental Implants & Full-Mouth Rehabilitation",
  "Smile Design & Cosmetic Veneers",
  "Clear Aligner & Braces Therapy",
  "Advanced Pediatric Dentistry",
  "Digital Smile Makeovers with CAD/CAM",
];

const awards = [
  { year: "2022", text: "Best Dental Practice Award — Kota Dental Association" },
  { year: "2020", text: "Featured Dentist — Rajasthan Health Excellence" },
  { year: "2018", text: "Certified Invisalign Provider" },
  { year: "2016", text: "Advanced Certification in Implantology, Germany" },
];

export default function Doctor() {
  const [settings, setSettings] = useState({ doctor_photo_url: "", doctor_bio: "", doctor_tagline: "" });

  useEffect(() => {
    document.title = "Dr. Deepika Charan — Manasvi Dental Clinic, Kota";
    api.get("/settings").then(r => setSettings(r.data)).catch(() => {});
  }, []);

  return (
    <div className="anim-fade-up">
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-16 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5 order-2 lg:order-1">
          <span className="text-xs uppercase tracking-[0.25em] text-primary" data-testid="doctor-badge">Meet Your Dentist</span>
          <h1 className="font-serif text-5xl sm:text-6xl tracking-tight mt-3">Dr. Deepika Charan</h1>
          <div className="mt-2 text-muted-foreground">{settings.doctor_tagline || "BDS, MIDA (Oro-Dental Surgeon) — Certified Implantologist & Orthodontist"}</div>
          <p className="mt-6 text-lg text-foreground/90 leading-relaxed">
            {settings.doctor_bio || "Dr. Deepika Charan is a highly-trained dental surgeon whose calm demeanour and precise craftsmanship have earned the trust of thousands of patients across Rajasthan. A certified implantologist and orthodontist, she blends the latest global techniques with a deeply personal touch."}
          </p>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            Trained across implantology and orthodontics, she brings the latest global dental techniques to Kota — the reason so many families consider Manasvi Dental Clinic their forever dental home.
          </p>
          <div className="mt-8 grid grid-cols-3 gap-6 max-w-md">
            <div><div className="font-serif text-3xl text-primary">12+</div><div className="text-xs text-muted-foreground uppercase tracking-widest mt-1">Years</div></div>
            <div><div className="font-serif text-3xl text-primary">8k+</div><div className="text-xs text-muted-foreground uppercase tracking-widest mt-1">Patients</div></div>
            <div><div className="font-serif text-3xl text-primary">4.9<span className="text-lg">★</span></div><div className="text-xs text-muted-foreground uppercase tracking-widest mt-1">Google</div></div>
          </div>
          <div className="mt-8">
            <Link to="/book"><Button size="lg" className="rounded-full bg-primary hover:bg-primary/90 h-12 px-8" data-testid="doctor-book-btn">Book with Dr. Deepika <ArrowRight className="ml-2 w-4 h-4" /></Button></Link>
          </div>
        </div>
        <div className="lg:col-span-7 order-1 lg:order-2 relative">
          <div className="rounded-[2rem] overflow-hidden aspect-[4/5] max-w-lg mx-auto shadow-xl bg-muted">
            {settings.doctor_photo_url && (
              <img src={settings.doctor_photo_url} className="w-full h-full object-cover" alt="Dr. Deepika Charan" data-testid="doctor-photo" />
            )}
          </div>
          <div className="hidden md:block absolute bottom-6 -left-4 bg-card p-5 rounded-2xl shadow-lg border border-border max-w-[220px]">
            <div className="flex gap-0.5 mb-2">{[...Array(5)].map((_,i)=><Star key={i} className="w-4 h-4 fill-accent text-accent" />)}</div>
            <p className="text-xs text-foreground/85 italic">&ldquo;Kota&apos;s most gentle dentist. Truly.&rdquo;</p>
            <div className="text-[10px] text-muted-foreground mt-2 uppercase tracking-widest">— Google Reviews</div>
          </div>
        </div>
      </section>

      <section className="bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-20 text-center">
          <Quote className="w-10 h-10 mx-auto opacity-60 mb-6" />
          <p className="font-serif text-3xl sm:text-4xl leading-relaxed italic">
            &ldquo;Every mouth tells a story. My job is to listen carefully — and then quietly restore what nature intended.&rdquo;
          </p>
          <div className="mt-8 text-primary-foreground/80 tracking-widest text-sm">— DR. DEEPIKA CHARAN</div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="text-xs uppercase tracking-[0.25em] text-primary mb-3">Qualifications</div>
        <h2 className="font-serif text-4xl sm:text-5xl tracking-tight">Trained. Certified. Trusted.</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
          {credentials.map((c, i) => (
            <div key={i} className="bg-card border border-border rounded-2xl p-6" data-testid={`credential-${i}`}>
              <div className="w-11 h-11 rounded-full bg-primary/15 grid place-items-center mb-4"><c.icon className="w-5 h-5 text-primary" /></div>
              <div className="font-serif text-2xl">{c.title}</div>
              <div className="text-sm text-muted-foreground mt-1">{c.subtitle}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-muted/40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-2 gap-12">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-primary mb-3">Areas of Expertise</div>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight">Six focus areas, mastered.</h2>
            <p className="mt-6 text-muted-foreground max-w-md">Dr. Deepika&apos;s clinical range spans preventive care to complex full-mouth reconstructions, all done in-house with the latest technology.</p>
          </div>
          <div className="grid gap-3">
            {expertise.map((e, i) => (
              <div key={i} className="bg-card border border-border rounded-2xl p-5 flex items-center gap-4" data-testid={`expertise-${i}`}>
                <CheckCircle2 className="w-5 h-5 text-primary shrink-0" />
                <span className="text-foreground/90">{e}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="text-xs uppercase tracking-[0.25em] text-primary mb-3">Awards &amp; Recognitions</div>
        <h2 className="font-serif text-4xl sm:text-5xl tracking-tight">A decade of recognition.</h2>
        <div className="mt-12 border-l-2 border-primary/30 pl-8 space-y-8 max-w-3xl">
          {awards.map((a, i) => (
            <div key={i} className="relative" data-testid={`award-${i}`}>
              <div className="absolute -left-[38px] top-1 w-4 h-4 rounded-full bg-primary" />
              <div className="text-sm text-muted-foreground tracking-widest">{a.year}</div>
              <div className="text-lg font-serif mt-1">{a.text}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-muted/60 border-y border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="font-serif text-3xl sm:text-4xl">Consult with Dr. Deepika today.</h2>
            <p className="mt-2 text-muted-foreground">{CLINIC.hours}</p>
          </div>
          <div className="flex gap-3">
            <Link to="/book"><Button size="lg" className="rounded-full bg-primary hover:bg-primary/90 h-12 px-8" data-testid="doctor-cta-book">Book Appointment</Button></Link>
            <Link to="/contact"><Button size="lg" variant="outline" className="rounded-full h-12 px-8" data-testid="doctor-cta-contact">Contact Clinic</Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
